import { useState } from 'react';

import needToPayIcon from '../../../assets/images/needToPay.svg';
import { closePopup, openPopup } from '../../../store/slices/popupSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  BEST_PRICE_MONTHS,
  DEFAULT_MONTHS,
  getDiscountLadderText,
  getPeriodPricing,
  MAX_MONTHS,
  MIN_MONTHS,
  pluralizeMonths,
  POPULAR_MONTHS,
} from '../../../utils/billing';
import { formatCurrency } from '../../../utils/functions';
import { OFFER_LINK_STYLE, OFFER_URL } from '../../../utils/links';
import TariffCardItem from '../../TariffCardItem/TariffCardItem';
import GreyButton from '../../ui/buttons/greyButton/GreyButton';
import RedButton from '../../ui/buttons/redButton/RedButton';
import Checkbox from '../../ui/checkbox/Checkbox';
import PayPeriodSelector from '../../ui/payPeriodSelector/PayPeriodSelector';
import styles from './styles/styles.module.css';

const ALL_MONTHS = Array.from(
  { length: MAX_MONTHS - MIN_MONTHS + 1 },
  (_, index) => MIN_MONTHS + index
);

function TopUpBalacePopup() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.data);
  const popupProps = useAppSelector((state) => state.popup.popupProps);
  const { monthlyRate, needToPay } = popupProps;
  const isLegalEntity = user.type !== 'individual';
  const isDebit = needToPay > 0;

  // Выбранный период в месяцах - единственный источник правды: и кнопки
  // 3/6/12 сверху, и выпадающий список со всеми периодами меняют его же.
  const [months, setMonths] = useState(DEFAULT_MONTHS);
  // true - оплачиваем только задолженность, выбор периода заблокирован.
  // Только для физлиц: у юрлиц переключателя нет, они выставляют счёт.
  const [isFormBlock, setFormBlock] = useState(isDebit && !isLegalEntity);

  const pricing = getPeriodPricing(monthlyRate, months);
  const periodLabel = pluralizeMonths(months);

  const periodOptions = ALL_MONTHS.map((value) => {
    const item = getPeriodPricing(monthlyRate, value);
    return {
      value,
      label: pluralizeMonths(value),
      price: `${formatCurrency(item.total)} ₽`,
      badge: item.percent > 0 ? `−${item.percent}%` : '',
    };
  });

  // || 0 - защита: если needToPay придёт не числом, кнопка должна
  // блокироваться, а не показывать "NaN ₽".
  const debt = parseFloat(needToPay) || 0;
  const amountToPay = isFormBlock ? debt : debt + pricing.total;

  const handleClosePopup = () => {
    dispatch(closePopup());
  };

  const handleCheckboxDebit = (isChecked) => {
    setFormBlock(!isChecked);
    setMonths(DEFAULT_MONTHS);
  };

  const handleClick = (payData) => {
    dispatch(closePopup('topUpBalance'));
    dispatch(
      openPopup({
        name: 'paymentMethods',
        props: {
          context: 'topUp',
          amount: payData,
        },
      })
    );
  };

  const handleInvoicePopupOpen = () => {
    dispatch(
      openPopup({
        name: 'invoice',
        props: {
          displayedPeriod: periodLabel,
          monthlyRate,
          discount: String(pricing.percent),
          total: formatCurrency(pricing.total),
          services: 'storage',
        },
      })
    );
  };

  return (
    <div className={styles.topUpBalancePopupWrapper}>
      <button
        className={styles.closePopupButton}
        onClick={handleClosePopup}
        aria-label="Закрыть"
      />
      <div className={styles.topUpBalanceTextWrapper}>
        <h2 className={styles.topUpBalanceTitle}>Пополнить баланс</h2>
        <p className={styles.topUpBalanceText}>Ваш тариф в месяц: {monthlyRate} ₽</p>
      </div>
      {!isLegalEntity && isDebit ? (
        <div className={styles.needToPayWrapper}>
          <div className={styles.needToPayText}>
            <img src={needToPayIcon} alt="Задолженность" />
            <div>
              <h2>Задолженность по тарифу</h2>
              <span>
                У вас имеется задолженность по тарифу за текущий период в размере{' '}
                {needToPay} ₽
              </span>
            </div>
          </div>
          <div className={styles.needToPayCheckbox}>
            <Checkbox
              label="Оплатить текущий период и пополнить баланс вперёд за несколько месяцев"
              checkboxName="payNeed"
              onChange={handleCheckboxDebit}
              smallSize
              labelFont={14}
            />
          </div>
        </div>
      ) : null}
      <div className={styles.topUpBalanceMainContentContainer}>
        <p className={styles.topUpBalanceSubtitle}>Период оплаты</p>
        <div className={styles.topUpBalanceCardsWrapper}>
          {POPULAR_MONTHS.map((value) => {
            const item = getPeriodPricing(monthlyRate, value);
            return (
              <TariffCardItem
                key={value}
                period={pluralizeMonths(value)}
                price={formatCurrency(item.total)}
                discount={String(item.percent)}
                oldPrice={formatCurrency(item.full)}
                badge={value === BEST_PRICE_MONTHS ? 'Лучшая цена' : ''}
                selected={months === value}
                onClick={() => setMonths(value)}
                disabled={isFormBlock}
              />
            );
          })}
        </div>
        <p className={styles.topUpBalanceLadder}>Скидка: {getDiscountLadderText()}</p>
        <div className={styles.topUpBalanceSelectorContainer}>
          <span className={styles.topUpBalanceSelectorCaption}>Выбранный период</span>
          <PayPeriodSelector
            options={periodOptions}
            value={months}
            onChange={setMonths}
            disabled={isFormBlock}
          />
        </div>
        <div className={styles.topUpBalanceSummaryBlock}>
          {!isLegalEntity && (
            <p className={styles.topUpBalanceSummaryText}>
              <span>Срок хранения: </span>
              <span className={styles.topUpBalanceSummaryStroke} />
              <span>{pricing.days} дн.</span>
            </p>
          )}
          <p className={styles.topUpBalanceSummaryText}>
            <span>Тариф: </span>
            <span className={styles.topUpBalanceSummaryStroke} />
            <span>{monthlyRate} ₽/мес</span>
          </p>
          <p className={styles.topUpBalanceSummaryText}>
            <span>Скидка: </span>
            <span className={styles.topUpBalanceSummaryStroke} />
            <span>{pricing.percent}%</span>
          </p>
          {pricing.saving > 0 ? (
            <p
              className={`${styles.topUpBalanceSummaryText} ${styles.topUpBalanceSummaryText_saving}`}
            >
              <span>Экономия: </span>
              <span className={styles.topUpBalanceSummaryStroke} />
              <span>{formatCurrency(pricing.saving)} ₽</span>
            </p>
          ) : null}
          {isDebit ? (
            <p className={styles.topUpBalanceSummaryTextRed}>
              <span>Задолженность: </span>
              <span className={styles.topUpBalanceSummaryStroke} />
              <span>{needToPay}</span>
            </p>
          ) : null}
        </div>
        {!isLegalEntity && (
          <p
            className={`${styles.topUpBalanceSummaryText} ${styles.topUpBalanceSummaryText_flexend}`}
          >
            Итого к оплате{' '}
            <span className={styles.topUpBalanceTotalText}>
              {formatCurrency(amountToPay)} ₽
            </span>
          </p>
        )}
        {!isLegalEntity && (
          <p className={styles.topUpBalanceNote}>
            * Расчётный период хранения — 30 календарных дней (п. 9.4.1{' '}
            <a
              href={OFFER_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={OFFER_LINK_STYLE}
            >
              Договора-оферты
            </a>
            ), а не календарный месяц. Например, 12 месяцев = 360 дней. Дату окончания
            оплаченного периода смотрите в разделе «Тарифы и оплата» после оплаты.
          </p>
        )}
      </div>
      <div
        className={`${styles.topUpBalanceTotalBlock} ${isLegalEntity ? styles.topUpBalanceTotalBlock_flexend : ''}`}
      >
        {isLegalEntity ? (
          <GreyButton
            text="Сформировать счёт"
            width={206}
            height={50}
            fontSize={16}
            onClick={handleInvoicePopupOpen}
            disabled={isFormBlock || pricing.total <= 0}
          />
        ) : null}
        {!isLegalEntity && isDebit ? (
          <RedButton
            text="Оплатить задолженность"
            width={285}
            height={50}
            disabled={!isFormBlock}
            onClick={() => handleClick(amountToPay)}
          />
        ) : null}
        <RedButton
          text={!isLegalEntity && isDebit ? 'Оплатить всё' : 'Выбрать карту'}
          width={!isLegalEntity ? 285 : 163}
          height={50}
          disabled={isFormBlock || amountToPay <= 0}
          onClick={() => handleClick(amountToPay)}
        />
      </div>
    </div>
  );
}

export default TopUpBalacePopup;
